/**
 * C1 — the Sharing drawer (figure C1), opened from S5's "Manage →".
 *
 * What this inventor shares with your portfolio, as THEY set it. The portfolio
 * can narrow it (switch extended visibility off) and can end the relationship;
 * it can never widen it — only the inventor grants.
 *
 * Replaces the v1 SponsorAccessEditor, which offered a seat-level chooser, a
 * Suspend button and sponsorship wording. None of those is in the figure and
 * none of them is a sharing act: a seat level is a licensing construct, and
 * suspending a seat says nothing about what anyone can see.
 */
import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import IpArrangementSection from '../sharing/IpArrangementSection'
import {
  EXTENDED_VISIBILITY_RULE,
  SHARING_IS_THEIRS_TO_GIVE,
  entitySharing,
  invitedAcceptedLine,
  sharesWithYouLine,
} from './sharing'
import type { SharingEntity } from './sharing'
import type {
  IpArrangementHolderRow, IpArrangementProposal,
} from '../../types/ipArrangements'

export interface SharingEditorProps {
  entity: SharingEntity
  /** The entity group this inventor sits in — the same company's other owners. */
  groupLabel: string
  /**
   * Saves a new entity-group name for this inventor, the same way the intake
   * is saved. Passed only for a Billing Owner or an Admin; without it the
   * field stays read-only rather than accepting an edit nobody can store.
   */
  onGroupLabelChange?: (value: string) => void
  /** The portfolio reading this drawer. */
  portfolioName: string
  /** How many of this inventor's projects are granted to this portfolio. */
  grantedCount: number
  /** The portfolio's own intake label for this inventor, when it has set one. */
  intake: string
  onIntakeChange: (value: string) => void
  onClose: () => void
  onSwitchOffExtendedVisibility: () => void
  onEndRelationship: () => void
  onInviteAsAnalyst?: () => void
  /** Only the billing owner may end a relationship; an admin sees it disabled. */
  canEndRelationship: boolean
  /** Folders this inventor has hidden from the portfolio, when it is known. */
  hiddenFolderCount?: number
  /**
   * The IP arrangements on record for THIS inventor, straight from
   * GET /innovateflow/ip-arrangements/{aggregatorId}/{entityId}. Counts only —
   * the route sends no owner id, name or email — so there is nothing here to
   * be careful with. The section is built and owned by the IP lane; this
   * drawer only supplies the data and the place.
   */
  ipArrangements?: IpArrangementHolderRow[]
  /** false only while a real read is in flight. */
  ipLoaded?: boolean
  /** A real read failure — never confused with "nothing on record". */
  ipError?: boolean
  /** Every project this inventor has granted this portfolio, for the picker. */
  ipGrantedProjects?: { id: string; name: string }[]
  /** The granted projects carrying no arrangement — "Inventor retains (default)". */
  ipProjectsWithout?: { id: string; name: string }[]
  /**
   * Puts an arrangement on record. Passed only for a Billing Owner or an Admin:
   * the API refuses an Analyst, so for them the link is absent rather than
   * present-and-rejected.
   */
  onProposeIpArrangement?: (proposal: IpArrangementProposal) => Promise<void>
}

/**
 * A drawer section. `title` is optional because figure C1's first section — the
 * Entity group and Intake pair — carries no heading of its own: the two fields
 * are labelled and that is the whole section. Round 2 added an "ENTITY GROUP
 * AND INTAKE" line for consistency with the three sections below it; the figure
 * does not have it, so it is gone.
 */
function Section({ title, aside, children }: { title?: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="border-t border-iflow-900/10 py-3 first:border-t-0">
      {(title || aside) && (
        <div className="mb-2 flex items-center gap-2">
          {title && <h3 className="text-[11px] font-bold uppercase tracking-wide text-iflow-700">{title}</h3>}
          {aside}
        </div>
      )}
      {children}
    </section>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-iflow-900/5 py-2 first:border-t-0">
      <span className="text-[12.5px] text-gray-500">{label}</span>
      <span className="text-right text-[12.5px] font-semibold text-iflow-900">{children}</span>
    </div>
  )
}

export default function SharingEditor({
  entity,
  groupLabel,
  onGroupLabelChange,
  portfolioName,
  grantedCount,
  intake,
  onIntakeChange,
  onClose,
  onSwitchOffExtendedVisibility,
  onEndRelationship,
  onInviteAsAnalyst,
  canEndRelationship,
  hiddenFolderCount,
  ipArrangements = [],
  ipLoaded = false,
  ipError = false,
  ipGrantedProjects = [],
  ipProjectsWithout = [],
  onProposeIpArrangement,
}: SharingEditorProps) {
  const sharing = entitySharing(entity, grantedCount)
  const [confirmingEnd, setConfirmingEnd] = useState(false)
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    closeRef.current?.focus()
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div data-part="SharingEditor" className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Close the sharing drawer"
        onClick={onClose}
        className="absolute inset-0 bg-iflow-900/30"
      />
      <aside
        role="dialog"
        aria-label={`Sharing — ${entity.entity_name}`}
        className="relative flex h-full w-full max-w-[470px] flex-col bg-white shadow-2xl"
      >
        <header className="flex items-start gap-3 border-b border-iflow-900/10 px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="flex items-center gap-2 text-[15.5px] font-bold text-iflow-900">
              Sharing — {entity.entity_name}
              <span
                className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-iflow-900/20 text-[10px] font-bold text-iflow-700"
                title={`What ${entity.entity_name} shares with your portfolio, as they set it. You can narrow it (switch extended visibility off) and you can end the relationship; you can never widen it — only they grant.`}
                aria-label="What this drawer can and cannot change"
              >
                ?
              </span>
            </h2>
            <p className="mt-0.5 text-[11.5px] text-gray-500">
              {invitedAcceptedLine(sharing, portfolioName)} ·{' '}
              <b className="text-iflow-900">shares {sharesWithYouLine(sharing, hiddenFolderCount)}</b>
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded p-1 text-gray-400 hover:text-gray-700"
          >
            <X size={18} aria-hidden />
          </button>
        </header>

        <div className="flex-1 overflow-auto px-5 pb-3">
          <Section>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="sharing-entity-group" className="block text-[11.5px] font-semibold text-iflow-800">
                  Entity group
                </label>
                <p className="text-[11px] text-gray-500">
                  groups this inventor with the same company&apos;s other inventors
                </p>
                <input
                  id="sharing-entity-group"
                  readOnly={!onGroupLabelChange}
                  value={groupLabel}
                  placeholder="e.g. Vanta Materials Group"
                  onChange={(e) => onGroupLabelChange?.(e.target.value)}
                  title={
                    onGroupLabelChange
                      ? 'The company label this portfolio files the inventor under. Yours to set; it never changes the inventor\u2019s own record.'
                      : 'Only a billing owner or an admin can change the entity group.'
                  }
                  className={`mt-1 w-full rounded-lg border border-iflow-900/15 px-2.5 py-1.5 text-[12.5px] text-iflow-900 ${
                    onGroupLabelChange ? 'bg-white' : 'bg-gray-50'
                  }`}
                />
              </div>
              <div>
                <label htmlFor="sharing-intake" className="block text-[11.5px] font-semibold text-iflow-800">
                  Intake
                </label>
                <p className="text-[11px] text-gray-500">set by you on the invite, editable here</p>
                <input
                  id="sharing-intake"
                  value={intake}
                  placeholder="e.g. Intake 2"
                  onChange={(e) => onIntakeChange(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-iflow-900/15 bg-white px-2.5 py-1.5 text-[12.5px] text-iflow-900"
                />
              </div>
            </div>
          </Section>

          <Section
            title="What you can see"
            aside={
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
                read-only mirror
              </span>
            }
          >
            <Row label="Projects granted">
              {grantedCount} project{grantedCount === 1 ? '' : 's'} granted
            </Row>
            <Row label="Extended visibility">
              <span className="flex items-center justify-end gap-2 whitespace-nowrap">
                <span
                  aria-hidden
                  className={`inline-block h-4 w-8 rounded-full ${sharing.extendedVisibility ? 'bg-iflow-500' : 'bg-gray-300'}`}
                >
                  <span className={`mt-0.5 block h-3 w-3 rounded-full bg-white ${sharing.extendedVisibility ? 'ml-4' : 'ml-1'}`} />
                </span>
                {sharing.extendedVisibility
                  ? `on — ${entity.entity_name} turned this on`
                  : 'off — only the inventor can turn it on'}
              </span>
            </Row>
            <div className="mt-2 flex items-center justify-between gap-3">
              <span className="text-[11.5px] text-gray-500">
                What you can <i>see</i> is set by the inventor, not here — {EXTENDED_VISIBILITY_RULE}.
              </span>
              {sharing.extendedVisibility && (
                <button
                  type="button"
                  onClick={onSwitchOffExtendedVisibility}
                  title="You can switch it off, never on"
                  className="shrink-0 rounded-lg border border-iflow-900/15 bg-white px-2.5 py-1 text-[12px] font-semibold text-iflow-800"
                >
                  Switch off
                </button>
              )}
            </div>
          </Section>

          {/* The IP arrangement, built and owned by the IP lane in
              components/sharing. It sits between "What you can see" and "A
              view into the portfolio?", which is where figure C1 puts it.
              It takes its horizontal padding from this drawer, like every
              other section — round 6 gave it its own, and its top border then
              ran 32px wider than the borders either side of it. */}
          <div>
            <IpArrangementSection
              arrangements={ipArrangements}
              loaded={ipLoaded}
              error={ipError}
              grantedProjects={ipGrantedProjects}
              projectsWithout={ipProjectsWithout}
              onSubmitProposal={onProposeIpArrangement}
            />
          </div>

          <Section title="A view into the portfolio?">
            <p className="text-[12.5px] leading-relaxed text-gray-600">
              To let this inventor see your portfolio, invite them to the team as an Analyst — a
              separate act. They would see the portfolio through the Analyst role, which you can
              narrow per project.{' '}
              <button
                type="button"
                onClick={onInviteAsAnalyst}
                className="font-semibold text-iflow-700 hover:text-iflow-800 hover:underline"
              >
                Invite as Analyst →
              </button>
            </p>
          </Section>
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-iflow-900/10 bg-gray-50 px-5 py-3">
          <span className="text-[11.5px] leading-snug text-gray-500">{SHARING_IS_THEIRS_TO_GIVE}</span>
          {confirmingEnd ? (
            <div className="shrink-0 text-right">
              <p className="max-w-[220px] text-[11px] text-gray-600">
                {entity.entity_name} will be told the relationship has ended. Nothing they shared
                stays visible to you.
              </p>
              <div className="mt-1 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmingEnd(false)}
                  className="text-[12px] text-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmingEnd(false)
                    onEndRelationship()
                  }}
                  className="rounded-lg border border-rose-300 px-2.5 py-1 text-[12px] font-semibold text-rose-700"
                >
                  Confirm end
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              disabled={!canEndRelationship}
              onClick={() => setConfirmingEnd(true)}
              title={
                canEndRelationship
                  ? 'Confirm quotes the notice the inventor will receive.'
                  : 'Only the billing owner can end this relationship'
              }
              className="shrink-0 whitespace-nowrap rounded-lg border border-rose-200 px-3 py-1.5 text-[12.5px] font-semibold text-rose-700 disabled:opacity-40"
            >
              End relationship
            </button>
          )}
        </footer>
      </aside>
    </div>
  )
}
